import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../context/LanguageContext';

const seoData = {
  en: {
    home: {
      title: 'Magnum Fountain SL | Premium Fountain Engineering & Turnkey Solutions',
      description: 'Magnum Fountain SL is a Spain-based fountain engineering company delivering complete fountain experiences worldwide — consulting, 3D design, equipment supply, and commissioning.',
    },
    about: {
      title: 'About Magnum Fountain SL | European Fountain Engineering Expertise',
      description: 'Founded in 2013, Magnum Fountain SL brings European engineering excellence to international fountain projects. Over 200 projects completed across 10+ countries.',
    },
    services: {
      title: 'Fountain Engineering Services | Consulting, Design & Commissioning',
      description: 'End-to-end fountain services: consulting, 3D simulation, DMX-512 show design, equipment supply, installation assistance, and commissioning for international projects.',
    },
    solutions: {
      title: 'Fountain Solutions | Show Fountains, Dry Deck, Urban & Hotel Fountains',
      description: 'Tailored fountain solutions for every application — show fountains, dry deck, urban plazas, hotel features, landmark fountains, and multimedia water experiences.',
    },
    process: {
      title: 'Our Process | From Concept to Commissioning | Magnum Fountain',
      description: 'A structured 10-step process from project brief to after-sales support. Transparent milestones and deliverables at every phase of your fountain project.',
    },
    consultants: {
      title: 'Technical Assistance for Architects & Consultants | Magnum Fountain',
      description: 'Specialized fountain engineering support for architects, engineering offices (BET), and consultants. Hydraulic design, BOQ development, and tender specification drafting.',
    },
    contact: {
      title: 'Contact Magnum Fountain SL | Request a Consultation',
      description: 'Get in touch with Magnum Fountain SL for your fountain project. Based in Madrid, Spain, we serve international projects. Free initial consultation available.',
    },
  },
  fr: {
    home: {
      title: 'Magnum Fountain SL | Ingénierie de Fontaines Premium & Solutions Clé en Main',
      description: 'Magnum Fountain SL est une entreprise espagnole spécialisée en ingénierie de fontaines, offrant des solutions complètes dans le monde entier — conseil, design 3D, fourniture et mise en service.',
    },
    about: {
      title: 'À Propos de Magnum Fountain SL | Expertise Européenne en Fontaines',
      description: "Fondée en 2013, Magnum Fountain SL apporte l'excellence d'ingénierie européenne aux projets de fontaines internationaux. Plus de 200 projets réalisés dans 10+ pays.",
    },
    services: {
      title: "Services d'Ingénierie de Fontaines | Conseil, Design & Mise en Service",
      description: 'Services fontaines de bout en bout : conseil, simulation 3D, spectacles DMX-512, fourniture, assistance installation et mise en service pour projets internationaux.',
    },
    solutions: {
      title: 'Solutions Fontaines | Spectacle, Sèches, Urbaines & Hôtelières',
      description: "Solutions fontaines adaptées à chaque application — spectacles, fontaines sèches, places urbaines, hôtels, monuments et jeux d'eau multimédia.",
    },
    process: {
      title: 'Notre Processus | Du Concept à la Mise en Service | Magnum Fountain',
      description: 'Un processus structuré en 10 étapes du brief projet au support après-vente. Jalons transparents et livrables à chaque phase de votre projet de fontaine.',
    },
    consultants: {
      title: 'Assistance Technique pour Architectes & Consultants | Magnum Fountain',
      description: "Support spécialisé en ingénierie de fontaines pour architectes, bureaux d'études (BET) et consultants. Design hydraulique, BOQ et rédaction de cahiers des charges.",
    },
    contact: {
      title: 'Contactez Magnum Fountain SL | Demander une Consultation',
      description: 'Contactez Magnum Fountain SL pour votre projet de fontaine. Basés à Madrid, Espagne, nous servons des projets internationaux. Consultation initiale gratuite.',
    },
  },
  es: {
    home: {
      title: 'Magnum Fountain SL | Ingeniería de Fuentes Premium y Soluciones Llave en Mano',
      description: 'Magnum Fountain SL es una empresa española de ingeniería de fuentes que ofrece soluciones completas a nivel mundial — consultoría, diseño 3D, suministro y puesta en marcha.',
    },
    about: {
      title: 'Sobre Magnum Fountain SL | Experiencia Europea en Fuentes',
      description: 'Fundada en 2013, Magnum Fountain SL aporta excelencia de ingeniería europea a proyectos de fuentes internacionales. Más de 200 proyectos en 10+ países.',
    },
    services: {
      title: 'Servicios de Ingeniería de Fuentes | Consultoría, Diseño y Puesta en Marcha',
      description: 'Servicios de fuentes de extremo a extremo: consultoría, simulación 3D, shows DMX-512, suministro de equipos, asistencia en instalación y puesta en marcha.',
    },
    solutions: {
      title: 'Soluciones de Fuentes | Espectáculo, Secas, Urbanas y Hoteleras',
      description: 'Soluciones de fuentes adaptadas a cada aplicación — espectáculos, fuentes secas, plazas urbanas, hoteles, monumentos y fuentes multimedia.',
    },
    process: {
      title: 'Nuestro Proceso | Del Concepto a la Puesta en Marcha | Magnum Fountain',
      description: 'Un proceso estructurado de 10 pasos desde el brief del proyecto hasta el soporte postventa. Hitos transparentes y entregables en cada fase.',
    },
    consultants: {
      title: 'Asistencia Técnica para Arquitectos y Consultores | Magnum Fountain',
      description: 'Soporte especializado en ingeniería de fuentes para arquitectos, oficinas de ingeniería (BET) y consultores. Diseño hidráulico, BOQ y especificaciones.',
    },
    contact: {
      title: 'Contacte Magnum Fountain SL | Solicitar Consulta',
      description: 'Contacte con Magnum Fountain SL para su proyecto de fuente. Con sede en Madrid, España, servimos proyectos internacionales. Consulta inicial gratuita.',
    },
  },
};

export default function SEO({ page }) {
  const { language } = useLanguage();
  const data = seoData[language]?.[page] || seoData.en[page] || {};

  return (
    <Helmet>
      <title>{data.title}</title>
      <meta name="description" content={data.description} />
      <meta property="og:title" content={data.title} />
      <meta property="og:description" content={data.description} />
      <meta property="og:type" content="website" />
      <meta name="twitter:title" content={data.title} />
      <meta name="twitter:description" content={data.description} />
      <html lang={language} />
    </Helmet>
  );
}
